import type { Signal } from 'typed-signals';
import type { Container } from 'pixi.js';
import type { TileStackModel } from './models/board/TileStackModel';
import { type TileColor } from './TileColor';
import type { TileStackView } from './views/board/TileStackView';
import type { LevelTaskProgress, LevelTaskType } from '../tasks/types';
import type { TileModel } from './models/board/TileModel';

export interface BoardState {
  cols: number;
  rows: number;
  hiddenStacks: HiddenStack[];
  stacks: TileStackState[];
  blockers?: BlockerExportState[];
}

export interface GridPosition {
  x: number;
  y: number;
}

export interface TileStackState extends GridPosition {
  tiles: TileColor[];
}

export type HiddenStack = GridPosition;

export interface TrayState {
  readonly colors: TileColor[][];
}

export enum BlockerType {
  Score = 'score',
  Ad = 'ad',
  Plank = 'plank',
  Tape = 'tape',
  Knot = 'knot',
  BirdHouse = 'birdHouse',
  Star = 'star',
  Rocket = 'rocket',
  Coin = 'coin',
  MeasureTape = 'measureTape',
}

export enum ConnectionStatus {
  Connected,
  WillBeRemoved,
  Removed,
}

export type BlockerMatchParams = {
  matchId: string;
  stack: TileStackModel;
  tilesRemoved: TileModel[];
  distantMatch?: boolean;
};

export interface BlockerModel {
  requestRemoval?: Signal<() => void>;
  onModelChanged?: Signal<() => void>;
  onReleaseCollectable?: Signal<() => void>;
  id: string;
  destructible: boolean;
  underTile: boolean;
  exportState(): BlockerState;
  getType(): BlockerType;
  postLevelSetupInit?: () => void;
  onMatchStart?: (stack: TileStackModel) => void;
  onMatch?: (params: BlockerMatchParams) => void;
  onScoreChanged?: (score: number) => void;
  onLevelBlockerTaskUpdate?: (taskType: LevelTaskType, progress: LevelTaskProgress) => void;
  onLevelTaskComplete?: (taskType: LevelTaskType) => void;
}

export interface AdBlockerState {
  type: BlockerType.Ad;
}

export interface CoinBlockerState {
  type: BlockerType.Coin;
  price: number;
}

export interface ScoreBlockerState {
  type: BlockerType.Score;
  score: number;
}

export interface PlankBlockerState {
  type: BlockerType.Plank;
  hp: number;
  color?: TileColor;
}

export interface TapeBlockerState {
  type: BlockerType.Tape;
  hp: number;
}

export interface KnotBlockerState {
  type: BlockerType.Knot;
  hp: number;
  connectedTo: GridPosition[];
}

export interface BirdHouseBlockerState {
  type: BlockerType.BirdHouse;
}

export interface StarBlockerState {
  type: BlockerType.Star;
}

export interface MeasureTapeBlockerState {
  type: BlockerType.MeasureTape;
  connectedTo: GridPosition[];
  isCase: boolean;
}

export interface RocketBlockerState {
  type: BlockerType.Rocket;
  hp: number;
}

export type BlockerState =
  | AdBlockerState
  | ScoreBlockerState
  | PlankBlockerState
  | TapeBlockerState
  | BirdHouseBlockerState
  | StarBlockerState
  | KnotBlockerState
  | CoinBlockerState
  | RocketBlockerState
  | MeasureTapeBlockerState;

export type BlockerExportState = BlockerState & GridPosition;

export interface BoardScheduler {
  wait(id: string | null, waitMs: number): Promise<void>;
  waitGenerator(id: string | null, waitMs: number): Generator<void, void>;
  update(deltaTime: number): void;
}

export interface BlockerView extends Container {
  getModel(): BlockerModel;
  onModelChanged?(): void;
  animateRemoval(): Promise<void>;
  setStackView?(stackView: TileStackView): void;
}

export interface ConnectedBlockerView extends BlockerView {
  updateConnections(): void;
}
